import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useGoogleLogin } from '@react-oauth/google'
import { accountApiService, accountQueries } from '@/services/accountApi'
import { GoogleContactsSyncResponse } from '@/types/account'

const CONTACTS_SCOPE = 'https://www.googleapis.com/auth/contacts.readonly'

// The redirect_uri used for auth-code flow with a popup is always 'postmessage'.
// This is the special value recognised by Google when the code is returned to
// the JavaScript origin rather than a redirect URL.
const POPUP_REDIRECT_URI = 'postmessage'

interface UseSyncGoogleContactsOptions {
  onSuccess?: (result: GoogleContactsSyncResponse) => void
  onError?: (error: Error) => void
}

/**
 * Hook that handles the full Google Contacts sync flow:
 *  1. Opens a Google OAuth popup requesting contacts.readonly scope
 *  2. Receives the authorization code
 *  3. Sends it to the backend for token exchange + People API sync
 *  4. Invalidates the contacts query cache on success
 *
 * Usage:
 *   const { openGoogleSync, isPending } = useSyncGoogleContacts({ onSuccess, onError })
 *   <Button onClick={openGoogleSync}>Sync Google Contacts</Button>
 */
export function useSyncGoogleContacts(options?: UseSyncGoogleContactsOptions) {
  const queryClient = useQueryClient()

  const syncMutation = useMutation({
    mutationFn: ({ code }: { code: string }) =>
      accountApiService.syncGoogleContacts(code, POPUP_REDIRECT_URI),
    onSuccess: (result) => {
      // Invalidate contacts so any UI showing the contact list refreshes
      queryClient.invalidateQueries({ queryKey: accountQueries.all })
      options?.onSuccess?.(result)
    },
    onError: (error: Error) => {
      options?.onError?.(error)
    },
  })

  const openGoogleSync = useGoogleLogin({
    flow: 'auth-code',
    scope: CONTACTS_SCOPE,
    onSuccess: (codeResponse) => {
      syncMutation.mutate({ code: codeResponse.code })
    },
    onError: (error) => {
      options?.onError?.(new Error(error.error_description ?? error.error ?? 'Google sign-in failed'))
    },
    onNonOAuthError: (error) => {
      // User closed the popup — not really an error, but surface it if needed
      if (error.type !== 'popup_closed') {
        options?.onError?.(new Error('Google sign-in popup error'))
      }
    },
  })

  return {
    /** Call this to open the Google OAuth popup and kick off the sync */
    openGoogleSync,
    /** True while the OAuth popup is open or the backend sync is in-flight */
    isPending: syncMutation.isPending,
    isSuccess: syncMutation.isSuccess,
    isError: syncMutation.isError,
    error: syncMutation.error,
    data: syncMutation.data,
  }
}
